import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ConfirmationModal from '../common/ConfirmationModal';
import { formatQuantity } from '../../utils/formatters';
import './IngredientList.css';

const IngredientList = ({ 
  ingredients, 
  isLoading, 
  onEdit, 
  onDelete,
  pageSize = 5 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const getStockStatus = (ingredient) => {
    if (!ingredient.current_stock && ingredient.current_stock !== 0) {
      return 'normal';
    }
    if (ingredient.current_stock === 0) {
      return 'out-of-stock';
    }
    if (ingredient.current_stock <= (ingredient.reorder_point || 0)) {
      return 'low-stock';
    }
    return 'normal';
  };

  const getStatusBadge = (ingredient) => {
    const stockStatus = getStockStatus(ingredient);
    
    if (stockStatus === 'out-of-stock') {
      return <span className="status-badge out-of-stock">Out of Stock</span>;
    }
    if (stockStatus === 'low-stock') {
      return <span className="status-badge low-stock">Low Stock</span>;
    }
    if (!ingredient.is_active) {
      return <span className="status-badge inactive">Inactive</span>;
    }
    return <span className="status-badge active">Active</span>;
  };

  const getRowStatusClass = (ingredient) => {
    const stockStatus = getStockStatus(ingredient);
    if (stockStatus === 'out-of-stock') return 'row-status-critical';
    if (stockStatus === 'low-stock') return 'row-status-warning';
    return 'row-status-normal';
  };

  // Filter ingredients based on search query
  const filteredIngredients = useMemo(() => {
    if (!ingredients || ingredients.length === 0) return [];
    
    if (!searchQuery.trim()) return ingredients;
    
    const query = searchQuery.toLowerCase();
    return ingredients.filter(ingredient => 
      ingredient.name?.toLowerCase().includes(query) ||
      ingredient.category?.toLowerCase().includes(query)
    );
  }, [ingredients, searchQuery]);

  // Paginate filtered ingredients
  const paginatedIngredients = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredIngredients.slice(startIndex, endIndex);
  }, [filteredIngredients, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredIngredients.length / pageSize);

  // Reset to page 1 when search query changes
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (isLoading) {
    return (
      <div className="ingredient-list-container">
        <div className="loading-state">
          <span className="spinner"></span> Loading ingredients...
        </div>
      </div>
    );
  }

  if (!ingredients || ingredients.length === 0) {
    return (
      <div className="ingredient-list-container">
        <div className="empty-state">
          No ingredients found. Click "Add Ingredient" to get started.
        </div>
      </div>
    );
  }

  return (
    <div className="ingredient-list-container">
      {/* Search Bar */}
      <div className="search-bar-section">
        <div className="search-input-wrapper">
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Search by name or category..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => handleSearchChange({ target: { value: '' } })}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          )}
        </div>
        <div className="search-results-info">
          {searchQuery && (
            <span className="results-count">
              Found {filteredIngredients.length} result{filteredIngredients.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Premium Table */}
      <div className="table-wrapper">
        <table className="ingredient-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Category</th>
            <th>Cost / Unit</th>
            <th>Supplier</th>
            <th>Stock Specs</th>
            <th>Status</th>
            <th className="col-actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedIngredients.length === 0 ? (
            <tr>
              <td colSpan="7" className="no-results">
                <div className="no-results-content">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  <p>No ingredients found</p>
                  {searchQuery && <span>Try adjusting your search</span>}
                </div>
              </td>
            </tr>
          ) : (
            paginatedIngredients.map((ingredient) => {
            // const stockStatus = getStockStatus(ingredient);
            const needsReorder = ingredient.current_stock != null
                && ingredient.reorder_point != null
                && ingredient.current_stock <= ingredient.reorder_point;
            
            return (
            <tr key={ingredient.id} className={getRowStatusClass(ingredient)}>
              <td>
                <div className="ingredient-name-cell">
                  <span className="font-weight-bold">{ingredient.name}</span>
                  {ingredient.is_packaging_material && (
                    <span className="status-badge packaging">Packaging</span>
                  )}
                </div>
                {ingredient.specifications && (
                  <div className="text-muted small">{ingredient.specifications}</div>
                )}
              </td>
              <td>{ingredient.category}</td>
              <td>
                <div>{formatCurrency(ingredient.cost_per_unit)}</div>
                <div className="text-muted small">per {ingredient.unit}</div>
              </td>
              <td>
                <div>{ingredient.supplier || '-'}</div>
                {ingredient.supplier_sku && (
                  <div className="text-muted small">SKU: {ingredient.supplier_sku}</div>
                )}
              </td>
              <td>
                <div className="stock-specs">
                  <div className="stock-spec-row">
                    <span className="spec-chip spec-chip--qty">
                      In stock: {formatQuantity(ingredient.current_stock ?? 0, ingredient.unit)} {ingredient.unit || ''}
                    </span>
                  </div>
                  <div className="stock-spec-row">
                    <span className="spec-chip">Min: {ingredient.min_stock_level != null ? formatQuantity(ingredient.min_stock_level, ingredient.unit) : '—'}</span>
                    <span className="spec-separator">|</span>
                    <span className="spec-chip">Reorder: {ingredient.reorder_point != null ? formatQuantity(ingredient.reorder_point, ingredient.unit) : '—'}</span>
                    <span className="spec-separator">|</span>
                    <span className="spec-chip">Max: {ingredient.max_stock_level != null ? formatQuantity(ingredient.max_stock_level, ingredient.unit) : '—'}</span>
                  </div>
                  {needsReorder && (
                    <div className="reorder-warning">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                      <span>Below reorder point</span>
                    </div>
                  )}
                </div>
              </td>
              <td>{getStatusBadge(ingredient)}</td>
              <td className="col-actions">
                <button
                  className="icon-btn edit"
                  onClick={() => onEdit(ingredient)}
                  title="Edit ingredient"
                  aria-label="Edit"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button
                  className="icon-btn delete"
                  onClick={() => {
                    setIngredientToDelete(ingredient);
                    setDeleteModalOpen(true);
                  }}
                  title="Delete ingredient"
                  aria-label="Delete"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                </button>
              </td>
            </tr>
            );
            })
          )}
        </tbody>
      </table>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        title="Delete Ingredient?"
        message="Are you sure you want to delete"
        itemName={ingredientToDelete?.name}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await onDelete(ingredientToDelete);
            setDeleteModalOpen(false);
            setIngredientToDelete(null);
          } catch (error) {
            console.error('Delete failed:', error);
          } finally {
            setIsDeleting(false);
          }
        }}
        onCancel={() => {
          setDeleteModalOpen(false);
          setIngredientToDelete(null);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        loadingText="Deleting..."
      />

      {/* Pagination Controls */}
      {filteredIngredients.length > 0 && totalPages > 1 && (
        <div className="pagination-section">
          <div className="pagination-info">
            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredIngredients.length)} of {filteredIngredients.length} ingredients
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn prev"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6"/>
              </svg>
              Prev
            </button>
            
            <div className="page-numbers">
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="page-ellipsis">...</span>
                ) : (
                  <button
                    key={page}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                )
              ))}
            </div>
            
            <button
              className="pagination-btn next"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

IngredientList.propTypes = {
  ingredients: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    category: PropTypes.string,
    unit: PropTypes.string,
    cost_per_unit: PropTypes.number,
    supplier: PropTypes.string,
    is_active: PropTypes.bool,
  })),
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  pageSize: PropTypes.number,
};

export default IngredientList;
